import React, { useEffect, useRef, useState } from 'react';
import './About.css';

function CountUp({ target, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

const stats = [
  { value: 150, suffix: '+', label: 'Projects Delivered', desc: 'Across 20+ countries' },
  { value: 98, suffix: '%', label: 'Client Retention', desc: 'They always come back' },
  { value: 40, suffix: '+', label: 'AI Models Deployed', desc: 'Production grade' },
  { value: 5, suffix: '+', label: 'Years of Dominance', desc: 'Since 2020' },
];

const values = [
  {
    icon: '◈',
    title: 'Precision',
    desc: 'Every line of code is crafted with military precision. We ship clean, scalable architecture.',
  },
  {
    icon: '◈',
    title: 'Innovation',
    desc: 'We operate at the bleeding edge of AI and web technology — always ahead of the curve.',
  },
  {
    icon: '◈',
    title: 'Loyalty',
    desc: 'Once you\'re part of the clan, you\'re protected. Long-term partnerships are our foundation.',
  },
  {
    icon: '◈',
    title: 'Execution',
    desc: 'Ideas die in planning. We execute fast, iterate faster, and deploy with confidence.',
  },
];

export default function About() {
  return (
    <section className="about section" id="about">
      <div className="grid-bg" />
      <div className="container">
        <div className="about-grid">
          {/* Left */}
          <div className="about-left">
            <span className="section-tag">Who We Are</span>
            <h2 className="section-title about-title">
              NOT AN AGENCY —<br />
              <span className="gradient-text">A WAR CLAN</span>
            </h2>
            <p className="about-desc">
              BlueClan is an elite collective of engineers, AI researchers, and digital architects
              operating from the vanguard of technology. We don't take on ordinary projects —
              we engage in missions that define industries.
            </p>
            <p className="about-desc">
              Founded on the principle that technology should be a weapon, not just a tool — every
              solution we build is engineered for dominance: faster, smarter, and more resilient
              than anything else on the field.
            </p>

            {/* Values */}
            <div className="values-grid">
              {values.map(v => (
                <div className="value-item" key={v.title}>
                  <span className="value-icon">{v.icon}</span>
                  <div>
                    <h4 className="value-title">{v.title}</h4>
                    <p className="value-desc">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <a href="#contact" className="btn-primary" style={{ marginTop: '36px' }}>
              <span>Join Our Ranks</span>
            </a>
          </div>

          {/* Right */}
          <div className="about-right">
            {/* Stats */}
            <div className="stats-grid">
              {stats.map(s => (
                <div className="stat-card glass-card" key={s.label}>
                  <div className="stat-value">
                    <CountUp target={s.value} suffix={s.suffix} />
                  </div>
                  <div className="stat-label">{s.label}</div>
                  <div className="stat-desc">{s.desc}</div>
                </div>
              ))}
            </div>

            {/* Terminal block */}
            <div className="terminal glass-card">
              <div className="terminal-header">
                <span className="t-dot red" /><span className="t-dot yellow" /><span className="t-dot green" />
                <span className="terminal-title">blueclan.sh</span>
              </div>
              <div className="terminal-body">
                <div className="t-line">
                  <span className="t-prompt">›</span>
                  <span className="t-cmd">./status --clan</span>
                </div>
                <div className="t-line t-output">
                  <span className="t-key">STATUS</span>
                  <span className="t-val t-green">● ACTIVE</span>
                </div>
                <div className="t-line t-output">
                  <span className="t-key">MEMBERS</span>
                  <span className="t-val">32 Elite Engineers</span>
                </div>
                <div className="t-line t-output">
                  <span className="t-key">MISSIONS</span>
                  <span className="t-val">150+ Completed</span>
                </div>
                <div className="t-line t-output">
                  <span className="t-key">TECH_STACK</span>
                  <span className="t-val t-cyan">Next.js · Python · AI</span>
                </div>
                <div className="t-line">
                  <span className="t-prompt">›</span>
                  <span className="t-cursor">_</span>
                </div>
              </div>
            </div>

            {/* Clan badge */}
            <div className="clan-badge glass-card">
              <div className="clan-badge-icon">
                <svg viewBox="0 0 60 60" fill="none">
                  <polygon points="30,4 56,18 56,42 30,56 4,42 4,18"
                    stroke="url(#badgeGrad)" strokeWidth="1.5" fill="rgba(0,212,255,0.06)"/>
                  <text x="30" y="38" textAnchor="middle" fontFamily="Orbitron,sans-serif"
                    fontSize="22" fontWeight="900" fill="url(#badgeGrad)">B</text>
                  <defs>
                    <linearGradient id="badgeGrad" x1="0" y1="0" x2="60" y2="60" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#00d4ff"/>
                      <stop offset="100%" stopColor="#7b2fff"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div>
                <div className="clan-badge-name">BlueClan</div>
                <div className="clan-badge-rank">Elite Technology Division</div>
              </div>
              <div className="clan-badge-verified">✓ VERIFIED</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
